import React, { Fragment, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectCurrentLanguage } from '../features/langSlice';
import { useGetContentsByTagWebQuery } from '../features/contentsApiSlice';
import CustomLink from './CustomLink';
import ProcessImg from '../assets/partner/Partner-riise-process.webp';
import RiiseImg from '../assets/partner/Partner-riise.webp';
import { useTranslation } from 'react-i18next';
import {
  CaseStudy,
  CaseStudyDesc,
  CaseStudyHeading,
  CaseStudyTitle,
  PartnerPageDesc,
  PartnerPageTitle,
  Section1Desc,
  Section1Heading,
  Section1Point1Desc,
  Section1Point1Title,
  Section1Point2Desc,
  Section1Point2Title,
  Section1Point3Desc,
  Section1Point3Title,
  Section2Desc,
  Section2Heading,
  Section2Point1Desc,
  Section2Point1Title,
  Section2Point2Desc,
  Section2Point2Title,
  Section2Point3Desc,
  Section2Point3Title,
} from '../Constants';

const CheckIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="10" cy="10" r="10" fill="var(--primary)" />
    <path d="M5.5 10.5L8.5 13.5L14.5 7" stroke="var(--tertiary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Partner = () => {
  const { t } = useTranslation();
  const selectedLang = useSelector(selectCurrentLanguage);
  const [lang, setLang] = useState();
  const [data, setData] = useState([]);
  const [skip] = useState(false);
  const [picture3, setPicture3] = useState('');

  const {
    data: fetchContents,
    isSuccess,
    refetch,
  } = useGetContentsByTagWebQuery({ tag: 'partner', lang: lang, skip: skip });

  useEffect(() => {
    setLang(selectedLang === 'us' ? 'en' : selectedLang);
    refetch();
  }, [refetch, lang, selectedLang]);

  useEffect(() => {
    if (isSuccess) {
      setData(fetchContents.data);
      if (fetchContents.data[0]) {
        setPicture3(`${process.env.REACT_APP_PUBLIC_URL}${fetchContents.data[5].picture}`);
      }
    }
  }, [fetchContents, isSuccess]);

  const section2Points = [
    { title: t(Section2Point1Title), desc: t(Section2Point1Desc) },
    { title: t(Section2Point2Title), desc: t(Section2Point2Desc) },
    { title: t(Section2Point3Title), desc: t(Section2Point3Desc) },
  ];

  return (
    <Fragment>
      {/* ── HERO ── */}
      <section style={{ background: 'var(--tertiary)', color: '#fff' }}>
        <div
          className="container t-mx-auto t-px-6 t-py-20 md:t-py-28"
          style={{ maxWidth: '900px' }}
        >
          <div className="t-text-center t-flex t-flex-col t-items-center t-gap-6">
            <h1
              className="t-text-3xl md:t-text-5xl t-leading-tight t-mb-0"
              style={{ fontFamily: "'DM Serif Display', serif", fontStyle: 'italic', wordBreak: 'break-word', overflowWrap: 'break-word' }}
            >
              {t(PartnerPageTitle).split(' ').map((word, i, arr) => {
                const accent = ['Partner', 'supply', 'chain', 'Lieferketten', 'Nahrungsergänzungsmittel-Lieferketten'];
                return accent.includes(word)
                  ? <span key={i} style={{ color: 'var(--primary)' }}>{word}{i < arr.length - 1 ? ' ' : ''}</span>
                  : <span key={i}>{word}{i < arr.length - 1 ? ' ' : ''}</span>;
              })}
            </h1>
            <p
              className="t-text-lg md:t-text-xl t-m-0 t-opacity-80"
              style={{ maxWidth: '620px', fontFamily: "'Plus Jakarta Sans', sans-serif" }}
            >
              {t(PartnerPageDesc)}
            </p>
            <div className="t-flex t-flex-col sm:t-flex-row t-gap-3 t-mt-2">
              {data[1] && (
                <CustomLink
                  to="https://app.lemcal.com/@paulantoine/30-minutes"
                  className="t-no-underline"
                >
                  <button
                    className="btn"
                    style={{
                      background: 'var(--primary)',
                      color: 'var(--tertiary)',
                      fontWeight: 700,
                      borderRadius: '6px',
                      padding: '12px 28px',
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      whiteSpace: 'nowrap',
                    }}
                    dangerouslySetInnerHTML={{ __html: data[1].title }}
                  />
                </CustomLink>
              )}
            </div>
            {data[2] && (
              <p
                className="t-m-0 t-opacity-60 t-text-sm"
                style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                dangerouslySetInnerHTML={{ __html: data[2].contents }}
              />
            )}
          </div>
        </div>
      </section>

      {/* ── SECTION 1 — Seamless execution ── */}
      <section style={{ background: 'var(--white, #F9F9F9)' }}>
        <div
          className="container t-mx-auto t-px-6 t-py-16 md:t-py-24 t-grid t-grid-cols-1 md:t-grid-cols-2 t-gap-12 t-items-center"
          style={{ maxWidth: '1040px' }}
        >
          <div className="t-flex t-flex-col t-gap-5">
            <p
              className="t-m-0 t-text-sm t-font-semibold t-uppercase t-tracking-widest"
              style={{ color: 'var(--secondary)', fontFamily: "'Plus Jakarta Sans', sans-serif" }}
            >
              {t(Section1Desc)}
            </p>
            <h2
              className="t-text-3xl md:t-text-4xl t-m-0 t-leading-snug"
              style={{ fontFamily: "'DM Serif Display', serif", color: 'var(--tertiary)', fontStyle: 'italic' }}
            >
              {t(Section1Heading)}
            </h2>
            <div className="t-flex t-flex-col t-gap-4 t-mt-2">
              {[
                { title: t(Section1Point1Title), desc: t(Section1Point1Desc) },
                { title: t(Section1Point2Title), desc: t(Section1Point2Desc) },
                { title: t(Section1Point3Title), desc: t(Section1Point3Desc) },
              ].map((item, i) => (
                <div key={i} className="t-flex t-gap-3 t-items-start">
                  <div className="t-flex-none t-mt-1">
                    <CheckIcon />
                  </div>
                  <p
                    className="t-m-0 t-text-base"
                    style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: 'var(--tertiary)' }}
                  >
                    <span className="t-font-bold">{item.title}: </span>
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
          <img
            src={ProcessImg}
            alt=""
            className="t-w-full t-h-auto"
            style={{ borderRadius: '16px', objectFit: 'cover' }}
          />
        </div>
      </section>

      {/* ── SECTION 2 — Why riise ── */}
      <section style={{ background: 'var(--tertiary)' }}>
        <div
          className="container t-mx-auto t-px-6 t-py-16 md:t-py-24 t-grid t-grid-cols-1 md:t-grid-cols-2 t-gap-12 t-items-center"
          style={{ maxWidth: '1040px' }}
        >
          <img
            src={RiiseImg}
            alt=""
            className="t-w-full t-h-auto t-order-2 md:t-order-1"
            style={{ borderRadius: '16px', objectFit: 'cover' }}
          />
          <div className="t-flex t-flex-col t-gap-5 t-order-1 md:t-order-2">
            <p
              className="t-m-0 t-text-sm t-font-semibold t-uppercase t-tracking-widest"
              style={{ color: 'var(--primary)', fontFamily: "'Plus Jakarta Sans', sans-serif" }}
            >
              {t(Section2Desc)}
            </p>
            <h2
              className="t-text-3xl md:t-text-4xl t-m-0 t-leading-snug"
              style={{ fontFamily: "'DM Serif Display', serif", color: '#fff', fontStyle: 'italic' }}
            >
              {t(Section2Heading)}
            </h2>
            <div className="t-flex t-flex-col t-gap-5 t-mt-2">
              {section2Points.map((item, i) => (
                <div
                  key={i}
                  className="t-flex t-flex-col t-gap-1 t-p-4"
                  style={{
                    background: 'rgba(255,255,255,0.07)',
                    borderRadius: '10px',
                    borderLeft: '3px solid var(--primary)',
                  }}
                >
                  <p
                    className="t-m-0 t-font-bold t-text-base"
                    style={{ color: 'var(--primary)', fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                  >
                    {item.title}
                  </p>
                  <p
                    className="t-m-0 t-text-sm t-opacity-80"
                    style={{ color: '#fff', fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                  >
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── CASE STUDY ── */}
      <section style={{ background: 'var(--white, #F9F9F9)', padding: '80px 24px' }}>
        <div className="container t-mx-auto" style={{ maxWidth: '760px' }}>
          <div
            className="t-flex t-flex-col t-gap-4 t-p-8 md:t-p-12"
            style={{
              background: 'var(--tertiary)',
              borderRadius: '20px',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: '24px',
                right: '24px',
                background: 'var(--primary)',
                color: 'var(--tertiary)',
                fontWeight: 700,
                fontSize: '12px',
                padding: '4px 14px',
                borderRadius: '999px',
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
              }}
            >
              {t(CaseStudy)}
            </div>
            <p
              className="t-m-0 t-text-sm t-font-semibold t-uppercase t-tracking-widest"
              style={{ color: 'var(--primary)', fontFamily: "'Plus Jakarta Sans', sans-serif" }}
            >
              {t(CaseStudyHeading)}
            </p>
            <h3
              className="t-text-2xl md:t-text-3xl t-m-0"
              style={{ fontFamily: "'DM Serif Display', serif", color: '#fff', fontStyle: 'italic' }}
            >
              {t(CaseStudyTitle)}
            </h3>
            <p
              className="t-m-0 t-text-base t-opacity-80"
              style={{ color: '#fff', fontFamily: "'Plus Jakarta Sans', sans-serif" }}
            >
              "{t(CaseStudyDesc)}"
            </p>
          </div>
        </div>
      </section>

      {/* ── FINAL CTA BANNER ── */}
      <section
        style={{
          backgroundColor: 'var(--tertiary)',
          backgroundImage: picture3
            ? `linear-gradient(rgba(0,59,77,0.88), rgba(0,59,77,0.92)), url(${picture3})`
            : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          padding: '80px 24px',
          color: '#fff',
        }}
      >
        <div
          className="container t-mx-auto t-text-center t-flex t-flex-col t-items-center t-gap-6"
          style={{ maxWidth: '700px' }}
        >
          {data[5] && (
            <h2
              className="t-text-3xl md:t-text-4xl t-m-0"
              style={{ fontFamily: "'DM Serif Display', serif", fontStyle: 'italic' }}
              dangerouslySetInnerHTML={{ __html: data[5].title }}
            />
          )}
          {data[5] && (
            <p
              className="t-m-0 t-text-lg t-opacity-80"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
              dangerouslySetInnerHTML={{ __html: data[5].contents }}
            />
          )}
          <CustomLink
            to="https://app.lemcal.com/@paulantoine/30-minutes"
            className="t-no-underline"
          >
            <button
              style={{
                background: 'var(--primary)',
                color: 'var(--tertiary)',
                fontWeight: 700,
                borderRadius: '6px',
                padding: '14px 32px',
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: '16px',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              {data[6] ? (
                <span dangerouslySetInnerHTML={{ __html: data[6].title }} />
              ) : (
                'Book a call'
              )}
            </button>
          </CustomLink>
          {data[7] && (
            <p
              className="t-m-0 t-opacity-60 t-text-sm"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
              dangerouslySetInnerHTML={{ __html: data[7].contents }}
            />
          )}
        </div>
      </section>
    </Fragment>
  );
};

export default Partner;
